// FILE: components/sections/BreakpointsSection.tsx
// This section documents the breakpoint system and responsive design principles.

import React from 'react';
import { SectionContainer, SubSection } from '../Content';
import { Breadcrumb } from '../Breadcrumb';
import CodeBlock from '../CodeBlock';

export const BreakpointsSection: React.FC<{ groupTitle: string; sectionTitle: string; id: string; }> = ({ groupTitle, sectionTitle, id }) => {
    return (
        <SectionContainer id={id}>
            <Breadcrumb group={groupTitle} section={sectionTitle} />
            <h2 className="section-title">Breakpoints & Responsive Design</h2>
            <p className="section-subtitle">
                Our design system follows a mobile-first approach with consistent breakpoints that ensure optimal user experience across all device sizes. All components are built with responsive behavior in mind.
            </p>
            
            <div className="space-y-6">
                {/* Breakpoint System */}
                <SubSection title="Breakpoint System">
                    <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-4 max-w-2xl">
                        The Acutrack Design System uses Tailwind CSS breakpoints to ensure consistent responsive behavior across all components and layouts.
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        <div className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-mono text-sm font-bold text-brand-orange">sm</span>
                                <span className="text-xs text-text-secondary dark:text-dark-text-secondary">640px+</span>
                            </div>
                            <p className="text-xs text-text-secondary dark:text-dark-text-secondary">Small devices, landscape phones</p>
                        </div>
                        <div className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-mono text-sm font-bold text-brand-orange">md</span>
                                <span className="text-xs text-text-secondary dark:text-dark-text-secondary">768px+</span>
                            </div>
                            <p className="text-xs text-text-secondary dark:text-dark-text-secondary">Medium devices, tablets</p>
                        </div>
                        <div className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-mono text-sm font-bold text-brand-orange">lg</span>
                                <span className="text-xs text-text-secondary dark:text-dark-text-secondary">1024px+</span>
                            </div>
                            <p className="text-xs text-text-secondary dark:text-dark-text-secondary">Large devices, laptops</p>
                        </div>
                        <div className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-mono text-sm font-bold text-brand-orange">xl</span>
                                <span className="text-xs text-text-secondary dark:text-dark-text-secondary">1280px+</span>
                            </div>
                            <p className="text-xs text-text-secondary dark:text-dark-text-secondary">Extra large devices, desktops</p>
                        </div>
                        <div className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                            <div className="flex items-center justify-between mb-2">
                                <span className="font-mono text-sm font-bold text-brand-orange">2xl</span>
                                <span className="text-xs text-text-secondary dark:text-dark-text-secondary">1536px+</span>
                            </div>
                            <p className="text-xs text-text-secondary dark:text-dark-text-secondary">2X large devices, large desktops</p>
                        </div>
                    </div>
                </SubSection>

                {/* Responsive Design Principles */}
                <SubSection title="Responsive Design Principles">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-2">📱 Mobile-First Approach</h4>
                            <p className="text-sm text-text-secondary dark:text-dark-text-secondary mb-4">
                                Start with mobile design and progressively enhance for larger screens. This ensures core functionality works on all devices.
                            </p>
                            <CodeBlock code={`// Mobile-first responsive classes
<div className="w-full md:w-1/2 lg:w-1/3">
  <p className="text-sm md:text-base lg:text-lg">Responsive text</p>
</div>`} />
                        </div>
                        <div>
                            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-2">🎯 Content Priority</h4>
                            <p className="text-sm text-text-secondary dark:text-dark-text-secondary mb-4">
                                Essential content and actions should be accessible on mobile. Secondary features can be revealed on larger screens.
                            </p>
                            <CodeBlock code={`// Hide/show based on screen size
<div className="hidden md:block">Desktop-only content</div>
<div className="md:hidden">Mobile-only content</div>`} />
                        </div>
                    </div>
                </SubSection>

                {/* Responsive Component Examples */}
                <SubSection title="Responsive Component Examples">
                    <div className="space-y-4">
                        <div>
                            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-2">Grid Layouts</h4>
                            <CodeBlock code={`// Responsive grid that adapts to screen size
<div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
  <div className="bg-bg-tertiary p-4 rounded">Item 1</div>
  <div className="bg-bg-tertiary p-4 rounded">Item 2</div>
  <div className="bg-bg-tertiary p-4 rounded">Item 3</div>
  <div className="bg-bg-tertiary p-4 rounded">Item 4</div>
</div>`} />
                        </div>
                        <div>
                            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-2">Navigation Patterns</h4>
                            <CodeBlock code={`// Responsive navigation that changes behavior
<nav className="flex flex-col md:flex-row gap-4">
  <div className="md:hidden">
    <button className="hamburger-menu">☰</button>
  </div>
  <div className="hidden md:flex space-x-6">
    <a href="#home">Home</a>
    <a href="#about">About</a>
    <a href="#contact">Contact</a>
  </div>
</nav>`} />
                        </div>
                        <div>
                            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-2">Typography Scaling</h4>
                            <CodeBlock code={`// Responsive typography that scales appropriately
<h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold">
  Responsive Heading
</h1>
<p className="text-sm sm:text-base lg:text-lg">
  Body text that scales with screen size
</p>`} />
                        </div>
                    </div>
                </SubSection>

                {/* Best Practices */}
                <SubSection title="Responsive Design Best Practices">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div className="space-y-3">
                            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-3">✅ Do's</h4>
                            <div className="flex items-start gap-2">
                                <span className="text-brand-orange font-bold">✓</span>
                                <span className="text-sm text-text-secondary dark:text-dark-text-secondary">Use relative units (rem, em) instead of fixed pixels</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-brand-orange font-bold">✓</span>
                                <span className="text-sm text-text-secondary dark:text-dark-text-secondary">Test on actual devices, not just browser resizing</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-brand-orange font-bold">✓</span>
                                <span className="text-sm text-text-secondary dark:text-dark-text-secondary">Consider touch targets (minimum 44px) for mobile</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-brand-orange font-bold">✓</span>
                                <span className="text-sm text-text-secondary dark:text-dark-text-secondary">Use flexbox and grid for flexible layouts</span>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-3">✗ Don'ts</h4>
                            <div className="flex items-start gap-2">
                                <span className="text-red-500 font-bold">✗</span>
                                <span className="text-sm text-text-secondary dark:text-dark-text-secondary">Don't hide critical content on mobile</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-red-500 font-bold">✗</span>
                                <span className="text-sm text-text-secondary dark:text-dark-text-secondary">Avoid horizontal scrolling on mobile</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-red-500 font-bold">✗</span>
                                <span className="text-sm text-text-secondary dark:text-dark-text-secondary">Don't rely on hover states for mobile</span>
                            </div>
                            <div className="flex items-start gap-2">
                                <span className="text-red-500 font-bold">✗</span>
                                <span className="text-sm text-text-secondary dark:text-dark-text-secondary">Avoid fixed positioning that blocks content</span>
                            </div>
                        </div>
                    </div>
                </SubSection>

                {/* Component Responsive Behavior */}
                <SubSection title="Component Responsive Behavior">
                    <p className="text-text-secondary dark:text-dark-text-secondary -mt-4 mb-4 max-w-2xl">
                        All Acutrack Design System components are built with responsive behavior built-in:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-2">Layout Components</h4>
                            <ul className="text-sm text-text-secondary dark:text-dark-text-secondary space-y-1">
                                <li>• Container adapts to screen size</li>
                                <li>• Grid automatically stacks on mobile</li>
                                <li>• Stack direction changes responsively</li>
                            </ul>
                        </div>
                        <div className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-2">Navigation Components</h4>
                            <ul className="text-sm text-text-secondary dark:text-dark-text-secondary space-y-1">
                                <li>• Sidebar collapses on mobile</li>
                                <li>• MegaMenu adapts to screen size</li>
                                <li>• Breadcrumbs wrap appropriately</li>
                            </ul>
                        </div>
                        <div className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-2">Form Components</h4>
                            <ul className="text-sm text-text-secondary dark:text-dark-text-secondary space-y-1">
                                <li>• Inputs stack vertically on mobile</li>
                                <li>• Button groups wrap responsively</li>
                                <li>• Date pickers adapt to touch</li>
                            </ul>
                        </div>
                        <div className="bg-bg-tertiary dark:bg-dark-bg-tertiary p-4 rounded-lg">
                            <h4 className="font-semibold text-text-primary dark:text-dark-text-primary mb-2">Data Components</h4>
                            <ul className="text-sm text-text-secondary dark:text-dark-text-secondary space-y-1">
                                <li>• Tables scroll horizontally on mobile</li>
                                <li>• Cards stack vertically on small screens</li>
                                <li>• Accordions maintain touch targets</li>
                            </ul>
                        </div>
                    </div>
                </SubSection>
            </div>
        </SectionContainer>
    );
};
